// Single env reader for the launcher package.

import { getAssistantOrigin } from './scriptOrigin';

export interface LauncherConfig {
  panelUrl: string;
  defaultApiBase: string;
}

function fromEnvOrDefault(name: 'VITE_PANEL_URL' | 'VITE_DEFAULT_API_BASE', fallback: string): string {
  try {
    const v = import.meta.env?.[name];
    return typeof v === 'string' && v.length > 0 ? v : fallback;
  } catch {
    return fallback;
  }
}

const ABSOLUTE_URL = /^https?:\/\//i;

/**
 * FEATURE-86 — resolve the panel iframe URL.
 *
 * Precedence:
 *   1. An absolute build-time `VITE_PANEL_URL` wins (prod bakes
 *      `https://assistant.booking1a.com/panel/`).
 *   2. A relative value (UAT bakes `/panel/`) is re-rooted at the launcher's
 *      own origin so a cross-origin host page still loads the panel from the
 *      assistant host — NOT from the host page's origin. This is the fix that
 *      makes one-tag cross-origin embedding work.
 *   3. No derivable origin → keep the relative value (today's same-origin
 *      behavior; fully backward compatible).
 */
export function resolvePanelUrl(envValue: string, origin: string | null): string {
  if (ABSOLUTE_URL.test(envValue)) return envValue;
  if (origin !== null) {
    try {
      return new URL(envValue, origin).toString();
    } catch {
      // fall through to the relative value
    }
  }
  return envValue;
}

/**
 * FEATURE-86 — resolve the launcher's default API base.
 *
 * Precedence: absolute env wins → explicit relative env kept → empty env
 * defaults to the derived assistant origin (so panel→BE stays same-origin to
 * the assistant host) → `''` (same-origin) when nothing is derivable.
 *
 * NOTE: this value is currently a convenience default only — `init()` always
 * carries an explicit `apiBase`. Kept correct for parity + future use.
 */
export function resolveApiBase(envValue: string, origin: string | null): string {
  if (ABSOLUTE_URL.test(envValue)) return envValue;
  if (envValue !== '') return envValue;
  return origin ?? '';
}

let cached: LauncherConfig | null = null;

export function config(): LauncherConfig {
  if (cached === null) {
    // Dev defaults point at the local pnpm-dev panel + BE servers.
    // UAT builds set VITE_PANEL_URL=/panel/ (relative) and leave
    // VITE_DEFAULT_API_BASE empty; FEATURE-86 re-roots those at the
    // launcher's own origin (derived from its <script src>) so the same
    // bundle works whether the host page is same-origin or cross-origin.
    const origin = getAssistantOrigin();
    cached = {
      panelUrl: resolvePanelUrl(fromEnvOrDefault('VITE_PANEL_URL', 'http://localhost:5174/'), origin),
      defaultApiBase: resolveApiBase(fromEnvOrDefault('VITE_DEFAULT_API_BASE', 'http://localhost:3000'), origin),
    };
  }
  return cached;
}

/** Test-only — clear cached config. */
export function __resetConfigForTests(): void {
  cached = null;
}
